// src/lib/print.js
//
// Page orientation for printing.
//
// `@page` is a top-level at-rule — it cannot be scoped to a class or an
// element — so the printable documents can't declare their own orientation
// in the stylesheet. Instead the orientation is swapped just before printing by
// rewriting a single injected <style> tag.
//
//   Order ticket  → landscape (a wide, boxed form, the way the shop's previous
//                   POS printed it)
//   Invoice       → portrait  (a normal customer-facing document)
//   Super ticket  → 'legal landscape' — a standard CSS page-size keyword for
//                   215.9 x 355.6mm (21.5 x 35.5cm), the shop's pre-printed
//                   continuous form. Printing at this size (rather than
//                   letter/A4) is what lets the printer driver's own
//                   paper-size-to-tray mapping route the job to the
//                   superticket tray automatically, once that mapping is
//                   configured once in the printer's Windows driver settings.

const STYLE_ID = 'agtpos-page-orientation';

function orientationStyle() {
  let el = document.getElementById(STYLE_ID);
  if (!el) {
    el = document.createElement('style');
    el.id = STYLE_ID;
    document.head.appendChild(el);
  }
  return el;
}

/**
 * These margins must stay in step with the `@page` rule in src/index.css —
 * this tag is injected into <head> and wins, so a value here silently
 * overrides the stylesheet.
 *
 * The ticket's top margin is deliberately larger than its sides: at an even
 * 13mm the heading was shaved off along the top edge of the sheet, while the
 * sides printed cleanly. The superticket gets no page margin at all — it's
 * pre-printed continuous stock, and every panel's position is controlled
 * directly by the .st- layout in index.css instead.
 *
 * @param {'landscape'|'portrait'|'superticket'} mode
 */
export function setPrintOrientation(mode) {
  const size   = mode === 'superticket' ? 'legal landscape' : mode;
  const margin = mode === 'landscape'   ? '17mm 13mm 13mm'
               : mode === 'superticket' ? '0'
               : '14mm 12mm';
  orientationStyle().textContent = `@page { size: ${size}; margin: ${margin}; }`;
}

/**
 * Print one of the documents with the right orientation, restoring the page
 * afterwards so the next print isn't affected by this one.
 *
 * @param {'ticket'|'invoice'|'superticket'} which
 */
export function printDocument(which) {
  const mode = which === 'invoice' ? 'portrait' : which === 'superticket' ? 'superticket' : 'landscape';
  setPrintOrientation(mode);
  if (which === 'invoice')     document.body.classList.add('printing-invoice');
  if (which === 'superticket') document.body.classList.add('printing-superticket');

  const cleanup = () => {
    document.body.classList.remove('printing-invoice', 'printing-superticket');
    setPrintOrientation('landscape');   // ticket is the common case
    window.removeEventListener('afterprint', cleanup);
  };
  window.addEventListener('afterprint', cleanup);
  window.print();
  // Safety net for browsers that never fire afterprint.
  setTimeout(cleanup, 3000);
}
